// Permissões apenas da nova área comercial local; não são roles da API atual.
export const permissionAreas = [
  { id: 'panel', label: 'Acesso ao painel', actions: ['access'] },
  { id: 'businessGroups', label: 'Grupos empresariais', actions: ['view', 'create', 'edit'] },
  { id: 'accountingOffices', label: 'Escritórios de contabilidade', actions: ['view', 'create', 'edit'] },
  { id: 'accountingUsers', label: 'Usuários da contabilidade', actions: ['view', 'create', 'edit'] },
  { id: 'companies', label: 'Empresas', actions: ['view', 'create', 'edit'] },
  { id: 'managers', label: 'Gestores do ERP', actions: ['view', 'create', 'edit'] },
  { id: 'subscriptions', label: 'Assinaturas / licenças', actions: ['view', 'create', 'edit'] },
  { id: 'plans', label: 'Planos destinados ao representante', actions: ['view'] },
  { id: 'tables', label: 'Tabela de preços / venda do representante', actions: ['view', 'edit'] },
  { id: 'panelUsers', label: 'Usuários do painel', actions: ['view', 'create', 'edit'] }
]
export const actionLabels = { access: 'Acessar', view: 'Visualizar', create: 'Cadastrar', edit: 'Editar' }
export const permissionIds = permissionAreas.flatMap(area => area.actions.map(action => `${area.id}.${action}`))
export const permissionPresets = [
  { id: 'consultation', label: 'Consulta', permissions: permissionIds.filter(id => id === 'panel.access' || (id.endsWith('.view') && !id.startsWith('panelUsers.'))) },
  { id: 'operational', label: 'Operacional', permissions: permissionIds.filter(id => !id.startsWith('panelUsers.')) },
  { id: 'administration', label: 'Administrador do representante', permissions: [...permissionIds] }
]

export function dependencies(id) {
  const [area, action] = id.split('.')
  const required = id === 'panel.access' ? [] : ['panel.access']
  if (action === 'create' || action === 'edit') required.push(`${area}.view`)
  if (area === 'accountingUsers' && action !== 'view') required.push('accountingOffices.view')
  if (area === 'companies' && action !== 'view') required.push('managers.view')
  if (area === 'subscriptions' && action !== 'view') required.push('companies.view', 'plans.view', 'tables.view')
  return [...new Set(required)]
}

export function permissionErrors(permissions) {
  if (!Array.isArray(permissions) || permissions.some(id => !permissionIds.includes(id))) return ['Selecione somente permissões válidas do painel.']
  const errors = []
  for (const id of permissions) if (dependencies(id).some(required => !permissions.includes(required))) errors.push('As permissões de cadastro/edição exigem visualização da área e dos vínculos relacionados, além do acesso ao painel.')
  return [...new Set(errors)]
}

export function updatePermissions(value, ids, checked, grantable = permissionIds) {
  const selected = new Set(value), allowed = new Set(grantable)
  if (checked) {
    const add = id => { if (!allowed.has(id)) return false; for (const required of dependencies(id)) if (!add(required)) return false; selected.add(id); return true }
    for (const id of ids) if (!add(id)) return [...value]
  } else {
    ids.forEach(id => { if (allowed.has(id)) selected.delete(id) })
    let changed
    do { changed = false; for (const id of [...selected]) if (dependencies(id).some(required => !selected.has(required))) { selected.delete(id); changed = true } } while (changed)
  }
  return [...selected]
}

export function panelIdentity(db, context) {
  const fromDb = (db?.panelUsers || []).find(user => String(user.id) === String(context?.panelUserId) && String(user.representativeId) === String(context?.representativeId))
  if (fromDb) return fromDb
  if (context?.permissions) {
    return {
      id: String(context.panelUserId || ''),
      representativeId: String(context.representativeId || ''),
      active: true,
      permissions: context.permissions
    }
  }
  return null
}
export function hasPanelAccess(db, context) {
  if (context?.role === 'master') return true
  if (!context?.representativeId) return false
  if (db?.representatives?.length > 0 && !db.representatives.some(rep => String(rep.id) === String(context.representativeId))) return false
  if (context?.role === 'representative') return true
  if (context?.role !== 'panelUser') return false
  const user = panelIdentity(db, context)
  return user?.active === true && permissionErrors(user.permissions).length === 0 && user.permissions.includes('panel.access')
}
export function hasPermission(db, context, id) {
  if (!permissionIds.includes(id) || !hasPanelAccess(db, context)) return false
  if (context?.role !== 'panelUser') return true
  const user = panelIdentity(db, context)
  return !!user && user.permissions.includes(id)
}
export function canAction(db, kind, action, context) {
  if (!hasPanelAccess(db, context)) return false
  if (context.role === 'master') return true
  if (kind === 'representatives') return ['view', 'edit'].includes(action) && context.role === 'representative'
  return hasPermission(db, context, `${kind}.${action}`)
}
export function canManagePanelUser(db, record, context) {
  if (!record || !Array.isArray(record.permissions)) return false
  if (!canAction(db, 'panelUsers', record?.id && (db.panelUsers || []).some(user => user.id === record.id) ? 'edit' : 'create', context)) return false
  if (context.role !== 'panelUser') return true
  const actor = panelIdentity(db, context)
  const current = (db.panelUsers || []).find(user => user.id === record.id)
  return (!current || current.representativeId === context.representativeId) && (!current || current.permissions.every(id => actor.permissions.includes(id))) && (record.permissions || []).every(id => actor.permissions.includes(id))
}

export const pathToResourceKind = {
  '/representantes': 'representatives',
  '/representantes/usuarios': 'panelUsers',
  '/planos': 'plans',
  '/planos/tabelas': 'tables',
  '/empresas': 'companies',
  '/empresas/gestores': 'managers',
  '/empresas/grupos': 'businessGroups',
  '/empresas/contabilidades': 'accountingOffices',
  '/empresas/usuarios-contabeis': 'accountingUsers',
  '/contabilidades': 'accountingOffices',
  '/contabilidades/usuarios': 'accountingUsers',
  '/assinaturas': 'subscriptions'
}

export function getUserSecurityContext(user) {
  if (!user) return { role: 'denied' }
  const isMaster = user.entityType === 'Proprietário da Conta' || user.accountType === 'ACCOUNT_OWNER' || user.entityType === 'ACCOUNT' || user.entityType === 'Account'
  if (isMaster) return { role: 'master' }
  const isPanelUser = !!user.userId || (Array.isArray(user.permissions) && user.permissions.length > 0)
  if (isPanelUser) {
    return {
      role: 'panelUser',
      panelUserId: String(user.userId || user.id),
      representativeId: String(user.representativeId || user.id || ''),
      permissions: Array.isArray(user.permissions) ? user.permissions : []
    }
  }
  const isRep = user.entityType === 'Representante' || user.entityType === 'REPRESENTATIVE' || user.representativeType != null
  if (isRep) {
    return {
      role: 'representative',
      representativeId: String(user.representativeId || user.id || '')
    }
  }
  return { role: 'denied' }
}

export function canUserAccessPath(user, path) {
  if (!user) return false
  const context = getUserSecurityContext(user)
  if (context.role === 'master' || context.role === 'representative') {
    return true
  }
  if (context.role === 'panelUser') {
    const targetPath = path === '/gestao-comercial' ? '/empresas' : path
    const kind = pathToResourceKind[targetPath]
    if (kind) {
      if (kind === 'representatives') return false
      return hasPermission(null, context, `${kind}.view`)
    }
    if (path.startsWith('/relatorios') || path.includes('/logs')) return false
    return true
  }
  return false
}

export function canUserAccessSection(user, secaoKey, secoes) {
  if (!user) return false
  const context = getUserSecurityContext(user)
  if (context.role === 'master' || context.role === 'representative') {
    return true
  }
  if (context.role === 'panelUser') {
    if (secaoKey === 'relatorios') return false
    const secao = secoes?.[secaoKey]
    if (!secao || !secao.tabs || secao.tabs.length === 0) return true
    return secao.tabs.some(tab => canUserAccessPath(user, tab.path))
  }
  return false
}

